import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useGlobalState } from "senf-shared";
import { cn } from "../../../../util/classNameConstructor";
import InvisibleInput from "../../../core/inputs/InvisibleInput";
import ListItem from "../../../core/list/ListItem";
import { pageTypeConfig } from "../pageTypes";
import { AlphaNum } from "../questions/ImageChoice";
import type { QuestionOption } from "../survey.types";
import { isChecked, toggle } from "../utils";
import { HandleItem } from "./HandleItem";

export const Row = ({
  option,
  checked,
  setChecked,
  rowIndex,
  provided,
  mode,
  type,
  secondaryContent,
  ...props
}: SelectProps & {
  setChecked: React.Dispatch<React.SetStateAction<string | string[]>>;
  options: QuestionOption[];
  option: QuestionOption;
  checked: string | string[];
  secondaryContent?: React.ReactNode;
}) => {
  const { t } = useTranslation();
  const [translation] = useGlobalState().translationsState;

  const [active, setActive] = useState(false);

  const disabled = (current: string | string[], val: string) => {
    return !!(
      props.multipleChoice &&
      props.maxChoices &&
      checked?.length >= props.maxChoices &&
      !isChecked(current, val, type)
    );
  };
  const hasConditions = props.conditions?.some((e) => e.type !== "none");

  return (
    <div className={cn("relative group/cell flex w-full items-center -mx-2")}>
      <HandleItem
        active={active}
        deletable={
          props.options.length >
          (pageTypeConfig[type]?.defaults?.minOptions || 0)
        }
        draggable={props.draggable}
        handleDelete={() => {
          props.removeItem(option.uid, "options", { focus: true });
        }}
        mode={mode}
        className="absolute -start-4"
        type="option"
      />
      <ListItem
        index={rowIndex}
        tag="div"
        noTooltip={mode === "edit"}
        className="w-full"
        primaryContentProps={{
          className: "line-clamp-none",
        }}
        primaryContent={
          <div className="flex items-center justify-between">
            {hasConditions && mode === "edit" && (
              <AlphaNum
                rowIndex={rowIndex}
                checked={isChecked(checked, option.uid, type)}
                className={"ml-2"}
              />
            )}
            <InvisibleInput
              id={option.uid}
              size="body-sm"
              value={translation?.[option.value] || option.value}
              // defaultValue={
              //   option.uid === "other" ? props.defaultOtherTitle : undefined
              // }
              onChange={({ currentTarget }) => {
                if (props.onChange) {
                  props.onChange(currentTarget);
                  return;
                }
                props.updateText(currentTarget.value, option.uid, "options");
              }}
              placeholder={
                props.placeholder?.(rowIndex) ||
                `${t("option")} ${rowIndex + 1}`
              }
              onClick={(e) => e.stopPropagation()}
              classNames={{
                container: "my-0.5 px-3",
              }}
              onFocus={() => setActive(true)}
              onBlur={() => setActive(false)}
              mode={props.editable === false ? "display" : mode}
              // markdown
            />
            {secondaryContent}
          </div>
        }
        classNames={{
          wrapper: "w-full",
          base: cn(
            "my-0.5 h-auto min-h-[38px] gap-0 py-0",
            mode === "edit" && "cursor-grab",
          ),
        }}
        lines={1}
        selectable
        selectableType={type}
        hoverable={mode === "display"}
        disabled={props.disabled || disabled(checked, option.uid)}
        disabledNotice={
          props.disabledNotice ||
          t("survey:disabledMessages.radio.maxChoices", {
            count: props.maxChoices,
          })
        }
        tabIndex={mode === "edit" ? -1 : rowIndex}
      />
    </div>
  );
};
